import { RecordResponse } from '@/models/ServiceTypes';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { contactsApi } from './contactsApi';
import { waPlatformQuery, waServicesQuery } from './baseQueries';
import { WARootState } from './store';

export interface DefaultFullControlResponse {
  DefaultFullControl: boolean;
  ShowAwayAccountsByDefault: boolean;
  HasBeenSet: boolean;
  ShowAwayAccountsGroupDefault: boolean;
}

/**
 * Represents a single setting returned from the advisorGroupSettings endpoint.
 * This is the format used in the legacy application.
 */
export interface AdvisorGroupSetting {
  Key: string;
  Value: string;
  // There are other properties, but these are the essential ones for localization.
}

/**
 * Represents the response structure for user settings and advisor group settings.
 * Both endpoints return the same format with Records array.
 */
export type SettingsResponse = RecordResponse<AdvisorGroupSetting>;

/**
 * Represents a single saved filter for investor reports.
 */
export interface SavedInvestorFilter {
  id?: string; // UUID for unique identification
  text: string;
  filters: {
    limit?: number | null;
    ExcludedCategories?: number[];
    ExcludedAccounts?: number[];
    ExcludedAccountTypes?: number[];
    ExcludedInstitutions?: number[];
    ExcludedTags?: number[];
    ExcludedGroups?: { GroupId: string; ExcludedIds: string[] }[];
  };
}

/**
 * Represents the request payload for updating user settings.
 */
export interface UpdateUserSettingRequest {
  Key: string;
  Value: string;
}

export const settingsApi = createApi({
  reducerPath: 'settingsApi',
  baseQuery: waServicesQuery,
  tagTypes: ['UserSettings', 'DefaultFullControl'],
  endpoints: (builder) => ({
    /**
     * Security Questions list
     */
    getSecurityQuestions: builder.query<
      { SecurityQuestionId: number; Label: string }[],
      void
    >({
      query: () => ({
        url: `/api/user/securityQuestions`,
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
        },
      }),
      transformResponse: (
        response: RecordResponse<{ SecurityQuestionId: number; Label: string }>,
      ) => response.Records ?? [],
    }),

    /**
     * Update one or more security answers. If a question selection changes, the
     * new answer must be provided with the new SecurityQuestionId.
     */
    updateSecurityAnswers: builder.mutation<
      boolean,
      { Answers: { SecurityQuestionId: number; Answer: string }[] }
    >({
      query: (body) => ({
        url: `/api/user/securityAnswers/`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['UserSettings'],
    }),

    /**
     * Security Answers for the current user
     */
    getSecurityAnswers: builder.query<
      { SecurityQuestionId: number; Answer: string }[],
      void
    >({
      query: () => ({
        url: `/api/user/securityAnswers`,
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
        },
      }),
      transformResponse: (
        response: RecordResponse<{
          SecurityQuestionId: number;
          Answer: string;
        }>,
      ) => response.Records ?? [],
      providesTags: ['UserSettings'],
    }),

    /**
     * Two-Factor / Account Recovery phone settings (shared payload)
     */
    getPhoneSettings: builder.query<
      {
        UseTwoFactorAuthentication: boolean;
        PhoneNumber: string | null;
        IsVerified: boolean;
        AlwaysAuthenticate: boolean;
        ForceAlwaysAuthenticate: boolean;
        Secret?: string;
        ForceTwoFactorForAdvisors?: boolean;
      },
      void
    >({
      query: () => ({
        url: `/api/user/phoneNumber`,
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
        },
      }),
    }),

    /**
     * Email Report frequency (Daily | Weekly | Monthly)
     */
    getEmailReportFrequency: builder.query<
      { Active: boolean; Interval: 'Daily' | 'Weekly' | 'Monthly' },
      void
    >({
      queryFn: async (_arg, api, extraOptions) => {
        const result = await waPlatformQuery(
          {
            url: `/Settings/GetFrequencyValue`,
            method: 'GET',
          },
          api,
          extraOptions,
        );

        if (result.error) return { error: result.error as FetchBaseQueryError };
        return {
          data: result.data as {
            Active: boolean;
            Interval: 'Daily' | 'Weekly' | 'Monthly';
          },
        };
      },
    }),

    /**
     * Verification PIN
     */
    getVerificationPin: builder.query<{ PIN: string | null }, void>({
      query: () => ({
        url: `/api/user/verificationPin`,
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
        },
      }),
    }),
    updateVerificationPin: builder.mutation<boolean, { PIN: string }>({
      query: (body) => ({
        url: `/api/user/verificationPin/`,
        method: 'PUT',
        headers: { 'content-type': 'application/json; charset=UTF-8' },
        body,
      }),
      invalidatesTags: ['UserSettings'],
    }),
    /**
     * Update password for the current user via platform endpoint.
     * Mirrors legacy UI behavior posting x-www-form-urlencoded payload.
     */
    updatePassword: builder.mutation<
      { success: boolean; errors?: string },
      { OldPassword: string; Password: string; ConfirmPassword: string }
    >({
      queryFn: async (
        { OldPassword, Password, ConfirmPassword },
        api,
        extraOptions,
      ) => {
        const result = await waPlatformQuery(
          {
            url: `/Account/UpdatePassword?Length=${encodeURIComponent(
              String(Password?.length ?? 0),
            )}`,
            method: 'POST',
            headers: {
              // Minimal headers to avoid CORS preflight
              'content-type':
                'application/x-www-form-urlencoded; charset=UTF-8',
            },
            body: new URLSearchParams({
              OldPassword,
              Password,
              ConfirmPassword,
            }).toString(),
          },
          api,
          extraOptions,
        );

        if (result.error) return { error: result.error as FetchBaseQueryError };
        return { data: result.data as { success: boolean; errors?: string } };
      },
    }),

    /**
     * Send verification code to phone number for account recovery
     */
    sendPhoneVerificationCode: builder.mutation<
      boolean,
      { phoneNumber: string; secret: string }
    >({
      query: (body) => ({
        url: `/api/user/phoneNumber//sendCode`,
        method: 'PUT',
        headers: {
          accept: 'application/json, text/javascript, */*; q=0.01',
          'content-type': 'application/json; charset=UTF-8',
        },
        body,
      }),
      transformErrorResponse: (response: {
        status: string | number;
        data?: unknown;
      }) => {
        // Handle API error responses with Message field
        const body = (response.data ?? undefined) as
          | Record<string, unknown>
          | undefined;
        const message =
          body && typeof body.Message === 'string'
            ? (body.Message as string)
            : undefined;
        if (message) {
          return { status: response.status, data: { message } };
        }
        return response;
      },
    }),

    /**
     * Verify phone code for account recovery
     */
    verifyPhoneCode: builder.mutation<
      boolean,
      { phoneNumber: string; code: string; secret: string }
    >({
      query: (body) => ({
        url: `/api/user/phoneNumber//verifyCode`,
        method: 'PUT',
        headers: {
          accept: 'application/json, text/javascript, */*; q=0.01',
          'content-type': 'application/json; charset=UTF-8',
        },
        body,
      }),
      transformErrorResponse: (response: {
        status: string | number;
        data?: unknown;
      }) => {
        // Handle API error responses with Message field
        const body = (response.data ?? undefined) as
          | Record<string, unknown>
          | undefined;
        const message =
          body && typeof body.Message === 'string'
            ? (body.Message as string)
            : undefined;
        if (message) {
          return { status: response.status, data: { message } };
        }
        return response;
      },
      invalidatesTags: ['UserSettings'],
    }),

    /**
     * Update phone settings including two-factor authentication
     */
    updatePhoneSettings: builder.mutation<
      boolean,
      {
        phoneNumber: string;
        UseTwoFactorAuthentication: boolean;
        PhoneNumber: string;
        IsVerified: boolean;
        ForceAlwaysAuthenticate: boolean;
        Secret: string;
        ForceTwoFactorForAdvisors?: boolean;
      }
    >({
      query: (body) => ({
        url: `/api/user/phoneNumber`,
        method: 'PUT',
        body,
      }),
      transformErrorResponse: (response: {
        status: string | number;
        data?: unknown;
      }) => {
        // Handle API error responses with Message field
        const body = (response.data ?? undefined) as
          | Record<string, unknown>
          | undefined;
        const message =
          body && typeof body.Message === 'string'
            ? (body.Message as string)
            : undefined;
        if (message) {
          return { status: response.status, data: { message } };
        }
        return response;
      },
      invalidatesTags: ['UserSettings'],
    }),
    getDefaultFullControl: builder.query<DefaultFullControlResponse, void>({
      queryFn: async (_arg, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const result = await baseQuery({
          url: `/api/${currentRole}/defaultFullControl`,
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        return { data: result.data as DefaultFullControlResponse };
      },
      extraOptions: { maxRetries: 3 },
      providesTags: ['DefaultFullControl'],
    }),

    /**
     * Update default full control settings (legacy-compatible payload)
     */
    updateDefaultFullControl: builder.mutation<
      DefaultFullControlResponse,
      {
        DefaultFullControl: boolean;
        ShowAwayAccountsByDefault: boolean;
        HasBeenSet: boolean;
        ShowAwayAccountsGroupDefault: boolean;
        ApplyToExistingAdvisors: boolean;
      }
    >({
      queryFn: async (body, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const result = await baseQuery({
          url: `/api/${currentRole}/defaultFullControl/`,
          method: 'PUT',
          headers: { 'content-type': 'application/json; charset=UTF-8' },
          body,
        });

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        return { data: result.data as DefaultFullControlResponse };
      },
      invalidatesTags: ['DefaultFullControl'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } finally {
          dispatch(
            contactsApi.util.invalidateTags([{ type: 'Contacts', id: 'LIST' }]),
          );
          dispatch(
            contactsApi.util.invalidateTags(['ContactAccountPermissions']),
          );
        }
      },
    }),

    /**
     * Fetches advisor-level settings used for localization and feature flags.
     * This endpoint returns an array of key-value pairs.
     */
    getAdvisorGroupSettings: builder.query<AdvisorGroupSetting[], void>({
      query: () => ({
        url: `/api/user/advisorGroupSettings`,
        method: 'GET',
      }),
      // The data is returned in a `Records` property from the legacy API.
      transformResponse: (response: { Records: AdvisorGroupSetting[] }) => {
        return response.Records || [];
      },
    }),

    /**
     * Fetches user-specific settings including grid configurations, saved filters,
     * and user preferences. This endpoint returns an array of key-value pairs.
     */
    getUserSettings: builder.query<AdvisorGroupSetting[], void>({
      query: () => ({
        url: `/api/user/settings/`,
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
        },
      }),
      // The data is returned in a `Records` property from the legacy API.
      transformResponse: (response: SettingsResponse) => {
        return response.Records || [];
      },
      providesTags: ['UserSettings'],
    }),

    /**
     * Updates a single user setting with optimistic updates.
     * The setting is identified by its Key.
     */
    updateUserSetting: builder.mutation<
      { Key: string; Value: string },
      { Key: string; Value: string }
    >({
      query: (setting) => ({
        url: `/api/user/settings`,
        method: 'POST',
        body: setting,
      }),
      async onQueryStarted({ Key, Value }, { dispatch, queryFulfilled }) {
        // Optimistic update: immediately update the cache
        const patchResult = dispatch(
          settingsApi.util.updateQueryData(
            'getUserSettings',
            undefined,
            (draft) => {
              const existing = draft.find((s) => s.Key === Key);
              if (existing) {
                existing.Value = Value;
              } else {
                draft.push({ Key, Value });
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          // On failure, revert the optimistic update
          patchResult.undo();
        }
      },
      // Still invalidate tags to ensure consistency after mutation
      invalidatesTags: ['UserSettings'],
    }),

    /**
     * Clears a user setting by posting an empty value.
     * Used to reset preferences to firm defaults.
     */
    clearUserSetting: builder.mutation<void, { Key: string }>({
      query: ({ Key }) => ({
        url: `/api/user/settings`,
        method: 'POST',
        body: { Key, Value: '' },
      }),
      async onQueryStarted({ Key }, { dispatch, queryFulfilled }) {
        // Optimistic update: remove from cache
        const patchResult = dispatch(
          settingsApi.util.updateQueryData(
            'getUserSettings',
            undefined,
            (draft) => {
              const index = draft.findIndex((s) => s.Key === Key);
              if (index !== -1) {
                draft.splice(index, 1);
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: ['UserSettings'],
    }),
  }),
});

export const {
  useGetDefaultFullControlQuery,
  useUpdateDefaultFullControlMutation,
  useGetAdvisorGroupSettingsQuery,
  useGetUserSettingsQuery,
  useUpdateUserSettingMutation,
  useClearUserSettingMutation,
  useGetSecurityQuestionsQuery,
  useGetSecurityAnswersQuery,
  useGetPhoneSettingsQuery,
  useGetEmailReportFrequencyQuery,
  useGetVerificationPinQuery,
  useUpdateVerificationPinMutation,
  useUpdateSecurityAnswersMutation,
  useUpdatePasswordMutation,
  useSendPhoneVerificationCodeMutation,
  useVerifyPhoneCodeMutation,
  useUpdatePhoneSettingsMutation,
} = settingsApi;
